import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import {
  CAPTION_STYLES,
  CAPTION_STYLES_PATH,
  CAPTION_STYLE_CTA,
  captionStyleGroupingLabel,
  captionStylePath,
  getCaptionStyle,
  otherCaptionStyles,
  type CaptionStyle,
} from "@/lib/caption-styles/content";

import CaptionStyleGrid from "@/components/caption-styles/CaptionStyleGrid";
import CaptionStylePreview from "@/components/caption-styles/CaptionStylePreview";
import CaptionStyleSpecs from "@/components/caption-styles/CaptionStyleSpecs";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import FancyButton from "@/components/ui/fancy-button";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";

interface CaptionStylePageProps {
  style: CaptionStyle | null;
}

const EYEBROW = "text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]";

const CaptionStylePage = ({ style: initialStyle }: CaptionStylePageProps) => {
  const router = useRouter();
  const slug = typeof router.query.style === "string" ? router.query.style : "";
  const style = initialStyle ?? (slug ? (getCaptionStyle(slug) ?? null) : null);

  if (!style) {
    if (!router.isReady) return null;

    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">
          The caption style you&apos;re looking for doesn&apos;t exist.
        </p>
        <FancyButton
          href={CAPTION_STYLES_PATH}
          semiFancy
          className="mt-8 w-fit rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
        >
          Browse caption styles
        </FancyButton>
      </div>
    );
  }

  const path = captionStylePath(style.slug);
  const crumbs = breadcrumbsForPath(path);
  const others = otherCaptionStyles(style.slug, 6);

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      <NextSeo
        title={`${style.seoTitle} | Crayo`}
        description={style.seoDescription}
        openGraph={{ images: [{ url: style.thumbnail, alt: `${style.name} caption style` }] }}
      />
      <JsonLd
        nodes={pageGraph({
          path,
          name: style.seoTitle,
          description: style.seoDescription,
          crumbs,
          faqs: style.faqs,
          speakable: [".answer-first"],
        })}
      />
      <Breadcrumbs items={crumbs} />

      <section className="mx-auto flex w-full max-w-screen-2xl flex-col px-4 pb-6 pt-8 md:px-12 md:pt-10 lg:px-[120px]">
        <div className="flex w-full max-w-[760px] flex-col gap-8">
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>Caption style</span>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-[#101828] sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
              {style.name} caption style
            </h1>
          </div>

          <CaptionStylePreview style={style} />

          <p className="answer-first text-[16px] leading-[1.7] text-[#101828] sm:text-[17px]">
            {style.answer}
          </p>

          <CaptionStyleSpecs spec={style.spec} />

          <div className="flex flex-col gap-4 text-[15px] leading-[1.7] text-[#525866] sm:text-[16px]">
            {style.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <div className="flex flex-col gap-4 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-8">
            <div className="flex flex-col gap-1">
              <span className="text-[15px] font-medium text-[#101828]">
                Use {style.name} on your next clip
              </span>
              <span className="text-[13px] text-[#525866]">
                Shows {captionStyleGroupingLabel(style.grouping)}. Included on every plan.
              </span>
            </div>
            <FancyButton
              href={CAPTION_STYLE_CTA}
              size="lg"
              inNewTab
              cta="caption_style_use"
              className="w-full whitespace-nowrap sm:w-auto"
            >
              Use this style
            </FancyButton>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-4">
              <span className={EYEBROW}>More caption styles</span>
              <Link
                href={CAPTION_STYLES_PATH}
                prefetch={false}
                className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#335CFF]"
              >
                All {CAPTION_STYLES.length} styles
                <ArrowRight className="size-3.5" strokeWidth={2.5} />
              </Link>
            </div>
            <CaptionStyleGrid styles={others} columns={3} compact />
          </div>
        </div>
      </section>

      <RelatedPages path={path} />
      <FAQ items={style.faqs} />
      <Footer />
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: CAPTION_STYLES.map((style) => ({ params: { style: style.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<CaptionStylePageProps> = async ({ params }) => {
  const slug = params?.style as string;
  const style = getCaptionStyle(slug) ?? null;

  return { props: { style } };
};

export default CaptionStylePage;
